// src/components/StripePaymentForm.js
import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { IoShieldCheckmark, IoAddOutline } from 'react-icons/io5';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': { color: '#aab7c4' }
        },
        invalid: { color: '#fa755a', iconColor: '#fa755a' }
    },
    hidePostalCode: true,
};

const StripePaymentForm = ({ onSuccess, clientSecret, onCancel, stripeCustomerId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState(null);
    const [processing, setProcessing] = useState(false);

    const [savedCards, setSavedCards] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState('new');
    const [loadingCards, setLoadingCards] = useState(false);

    useEffect(() => {
        if (!stripeCustomerId) return;
        setLoadingCards(true);
        const listPMs = httpsCallable(functions, 'listPaymentMethods');
        listPMs({ stripeCustomerId })
            .then(res => {
                const methods = res.data.paymentMethods || [];
                setSavedCards(methods);
                if (methods.length > 0) {
                    setSelectedCardId(methods[0].id); // Default to first saved card
                }
            })
            .catch(console.error)
            .finally(() => setLoadingCards(false));
    }, [stripeCustomerId]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements || !clientSecret) return;

        setProcessing(true);
        setErrorMessage(null);

        let confirmConfig = {};

        if (selectedCardId !== 'new') {
            confirmConfig = {
                payment_method: selectedCardId
            };
        } else {
            const cardElement = elements.getElement(CardElement);
            confirmConfig = {
                payment_method: {
                    card: cardElement,
                }
            };
        }

        const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, confirmConfig);

        if (error) {
            setErrorMessage(error.message);
            setProcessing(false);
        } else if (paymentIntent.status === 'succeeded') {
            setProcessing(false);
            if (onSuccess) onSuccess();
        }
    };

    const getCardLogo = (brand) => {
        switch (brand) {
            case 'visa': return "💳 Visa";
            case 'mastercard': return "💳 MC";
            case 'amex': return "💳 Amex";
            default: return "💳";
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {loadingCards ? (
                <div style={{ textAlign: 'center', marginBottom: 15, fontSize: '0.9rem', color: '#666' }}>
                    Cargando métodos de pago guardados...
                </div>
            ) : (
                <>
                    {savedCards.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '0.95rem' }}>Selecciona un método de pago:</p>
                            {savedCards.map(pm => (
                                <label 
                                    key={pm.id} 
                                    style={{
                                        display: 'block', padding: '12px', border: selectedCardId === pm.id ? '2px solid #6366f1' : '1px solid #cbd5e1', 
                                        borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', background: selectedCardId === pm.id ? '#e0e7ff' : '#fff'
                                    }}
                                >
                                    <input 
                                        type="radio" 
                                        name="paymentMethod" 
                                        value={pm.id} 
                                        checked={selectedCardId === pm.id} 
                                        onChange={() => setSelectedCardId(pm.id)} 
                                        style={{ marginRight: '10px' }}
                                    />
                                    <strong>{getCardLogo(pm.card.brand)}</strong> terminada en {pm.card.last4}
                                </label>
                            ))}
                            
                            <label 
                                style={{
                                    display: 'block', padding: '12px', border: selectedCardId === 'new' ? '2px solid #6366f1' : '1px solid #cbd5e1', 
                                    borderRadius: '8px', cursor: 'pointer', background: selectedCardId === 'new' ? '#e0e7ff' : '#fff'
                                }}
                            >
                                <input 
                                    type="radio" 
                                    name="paymentMethod" 
                                    value="new" 
                                    checked={selectedCardId === 'new'} 
                                    onChange={() => setSelectedCardId('new')} 
                                    style={{ marginRight: '10px' }}
                                />
                                <strong><IoAddOutline style={{transform: 'translateY(2px)'}}/> Usar una nueva tarjeta</strong>
                            </label>
                        </div>
                    )}

                    {(selectedCardId === 'new' || savedCards.length === 0) && !loadingCards && (
                        <div style={{ marginBottom: '20px', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', background: 'white' }}>
                            <CardElement options={CARD_ELEMENT_OPTIONS} />
                        </div>
                    )}
                </>
            )}

            {errorMessage && <div style={{ color: '#ef4444', marginBottom: '10px', fontSize: '0.9rem' }}>{errorMessage}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={processing}
                    className="form-button secondary"
                    style={{ flex: 1, backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' }}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    disabled={!stripe || processing || loadingCards}
                    className="form-button"
                    style={{ flex: 2, backgroundColor: '#6772e5' }}
                >
                    {processing ? 'Procesando...' : (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <IoShieldCheckmark /> Pagar Ahora
                        </span>
                    )}
                </button>
            </div>
            <p style={{ marginTop: '10px', fontSize: '0.8em', color: '#666', textAlign: 'center' }}>
                Pagos seguros procesados por Stripe
            </p>
        </form>
    );
};

export default StripePaymentForm;
